import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Scale, Trash2 } from "lucide-react";
import { PageHeader, Stat } from "./Sugar";
import { useAuth } from "../contexts/AuthContext";

function bmiCat(b) {
  if (b < 18.5) return { l: "Underweight", c: "amber" };
  if (b < 25) return { l: "Normal", c: "emerald" };
  if (b < 30) return { l: "Overweight", c: "amber" };
  return { l: "Obese", c: "rose" };
}

export default function Weight() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [w, setW] = useState("");
  const [waist, setWaist] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => { try { const { data } = await api.get("/weight"); setLogs(data); } catch {} };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await api.post("/weight", { weight_kg: Number(w), waist_cm: waist ? Number(waist) : undefined, date });
      setW(""); setWaist(""); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/weight/${id}`); await load(); };

  const h = user?.height_cm ? user.height_cm / 100 : null;
  const computeBmi = (kg) => (h ? +(kg / (h * h)).toFixed(1) : null);
  const chartData = [...logs].reverse().map((l) => ({ t: new Date(l.measured_at || l.created_at).toLocaleDateString(), weight: l.weight_kg }));

  const latest = logs[0];
  const latestBmi = latest ? computeBmi(latest.weight_kg) : null;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="weight-page">
      <PageHeader icon={Scale} title="Weight & BMI" subtitle="Monitor body weight and BMI category" accent="emerald" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 stagger">
        <Stat label="Latest weight" value={latest ? latest.weight_kg : "—"} unit="kg" />
        <Stat label="BMI" value={latestBmi || "—"} unit="" accent={latestBmi ? bmiCat(latestBmi).c : "blue"} />
        <Stat label="Entries" value={logs.length} unit="" accent="emerald" />
      </div>

      {!user?.height_cm && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-4">Add your height in your <a href="/profile" className="font-semibold underline">profile</a> to compute BMI.</div>
      )}

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="weight-form">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</Label>
          <Input type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="weight-date" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Weight (kg)</Label>
          <Input type="number" step="0.1" required value={w} onChange={(e) => setW(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="weight-value" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Waist (cm)</Label>
          <Input type="number" value={waist} onChange={(e) => setWaist(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="weight-waist" />
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="weight-submit">{loading ? "Saving…" : "Add"}</Button>
        </div>
        {error && <div className="sm:col-span-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="weight-error">{error}</div>}
      </form>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="weight-chart">
        <h3 className="font-heading font-semibold text-slate-800 mb-3">Trend</h3>
        <div className="h-64">
          {chartData.length === 0 ? (
            <div className="h-full grid place-items-center text-slate-400 text-sm">Add entries to see your trend</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="t" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }} />
                <Line type="monotone" dataKey="weight" stroke="#10b981" strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <h3 className="font-heading font-semibold text-slate-800 mb-3">Recent entries</h3>
      <div className="space-y-2" data-testid="weight-list">
        {logs.length === 0 && <div className="text-sm text-slate-400">No entries yet.</div>}
        {logs.map((l) => {
          const b = computeBmi(l.weight_kg);
          const cat = b ? bmiCat(b) : null;
          return (
            <div key={l.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">{l.weight_kg} kg{l.waist_cm ? ` • waist ${l.waist_cm} cm` : ""}</div>
                <div className="text-xs text-slate-500">{new Date(l.measured_at || l.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-3">
                {cat && <span className={`text-xs font-semibold px-2.5 py-1 rounded-full bg-${cat.c}-50 text-${cat.c}-700 border border-${cat.c}-200`}>{cat.l} ({b})</span>}
                <button onClick={() => del(l.id)} data-testid={`weight-del-${l.id}`} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
